import React, { useEffect, useState } from "react";
import {
  fetchContact,
  deleteContact,
  saveContact,
} from "../../lib/fetch/contacts";
import { useTranslation } from "react-i18next";
import { useAlert } from "react-alert";
import { useAuth } from "../../components/Auth";
import { useLoading } from "../../components/Loading";
import ContactDetailPresentational from "./ContactDetailPresentational";
import { prepareActivity } from "../../lib/helpers/activities";
import createActivity from "../../lib/fetch/createActivity";
import {
  fetchActivities,
  upsertActivity,
  deleteActivity,
} from "../../lib/fetch/activities";
import { createCompany } from "../../lib/fetch/company";
import { isAdminRole } from "../../constants/user";

function ContactDetail(props) {
  const { match, history } = props;
  const { params: { id } = {} } = match;
  const [contact, setContact] = useState({});
  const [activities, setActivities] = useState([]);
  const [t] = useTranslation("CONTACT_DETAIL");
  const [{ token, profile = {} } = {}] = useAuth();
  const { role, id: currentUserId } = profile;
  const isAdmin = isAdminRole(role);
  const alert = useAlert();
  const { showLoading, hideLoading } = useLoading();
  const [saveStatus, setSaveStatus] = useState("idle"); // idle, saving, success, error

  useEffect(() => {
    const fetch = async (id) => {
      const { error, data } = await fetchContact({ id, token });

      if (error) {
        alert.error(t("COMMON:GENERIC_ERROR"));
        return;
      }
      setContact(data);
      await fetchListActivities(id);
    };

    Number(id) && fetch(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSave = async (values) => {
    setSaveStatus("saving");
    const { error, data } = await saveContact({ id, token, data: values });

    if (error) {
      setSaveStatus("error");
      return;
    }

    setContact(data);
    setSaveStatus("success");
  };

  const onDelete = async () => {
    const { error } = await deleteContact({ token, id });

    if (error) {
      alert.error(t("COMMON:GENERIC_ERROR"));
      return;
    }

    history.push("/contacts");
    alert.success(t("COMMON:DELETE_SUCCESS"));
  };

  const onSaveActivity = async (values, type, activityId) => {
    const data = prepareActivity({ activity: values, type, contact_id: id, company_id: contact.company_id });

    const { error } = activityId
      ? await upsertActivity({ token, data, id: activityId })
      : await createActivity({ token, data });

    if (error) {
      alert.error(t("COMMON:GENERIC_ERROR"));
      return;
    }

    alert.success(t("COMMON:SAVE_SUCCESS"));
    fetchListActivities(id);
  };

  const fetchListActivities = async (contactId) => {
    const { error, data } = await fetchActivities({
      contactId,
      token,
      limit: 10000000,
    });

    if (error) {
      setActivities([]);
      alert.error(t("COMMON:GENERIC_ERROR"));
      return;
    }
    setActivities(data);
  };

  const onDeleteActivity = async (activityId) => {
    showLoading();
    const { error } = await deleteActivity({ token, id: activityId });
    hideLoading();

    if (error) {
      alert.error(t("COMMON:GENERIC_ERROR"));
      return;
    }

    alert.success(t("COMMON:DELETE_SUCCESS"));
    fetchListActivities(id);
  };

  const onCreateCompany = async () => {
    const defaultCompany = {
      name: t('COMMON:NEW_COMPANY'),
      annual_budget: 0,
      annual_revenue: 0,
      bank_credit: 0,
      type: 'client',
      ...(!isAdmin && { user_id: currentUserId }),
    };

    const { error, data } = await createCompany({ data: defaultCompany, token });

    if (error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
      return;
    }

    // Update the contact with the new company_id
    await onSave({ company_id: data.id });

    history.push(`/company-detail/${data.id}/info`);
  };

  return (
    <ContactDetailPresentational
      contact={contact}
      onSave={onSave}
      onSaveActivity={onSaveActivity}
      activities={activities}
      onDelete={onDelete}
      onDeleteActivity={onDeleteActivity}
      onCreateCompany={onCreateCompany}
      saveStatus={saveStatus}
      onSaveStatusChange={setSaveStatus}
      {...props}
    />
  );
}
export default ContactDetail;
